import { Navigate, Outlet } from 'react-router';
import { useAuthClient } from '@/lib/authClient';
import { useMyOrganization } from '@/hooks/organization/use-my-organization';
import { useDeviceConnection } from '@/hooks/use-device-connection';
import { useAccounts } from '@/hooks/account/useAccounts';
import { setOrgCookie } from '@/lib/api/orgCookie';
import { ORG_COOKIE_NAME } from '@mls-wallet/shared-types';

function getCookieValue(name: string): string | null {
  const cookies = document.cookie.split(';').map((c) => c.trim());
  const prefix = `${name}=`;
  for (const cookie of cookies) {
    if (cookie.startsWith(prefix)) return cookie.slice(prefix.length) || null;
  }
  return null;
}

/**
 * Auth guard that requires:
 * - User is signed in
 * - User has org membership
 * - Organization has NO accounts
 *
 * Behavior:
 * - Returns null while loading org + accounts (prevents UI flash)
 * - If org has accounts, redirects to /accounts
 * - If org has no accounts, allows access (renders child routes)
 *
 * Important:
 * - Accounts fetch is gated to run only after org is known.
 * - We synchronously set the org cookie before fetching accounts.
 */
export function RequireOrgHasNoAccounts() {
  const { session } = useAuthClient();
  const { isChecking, isConnected } = useDeviceConnection();
  const { organization, isLoading: isOrgLoading } = useMyOrganization({
    // Avoid calling /organization/my when mobile is disconnected.
    enabled: isConnected,
  });
  const orgId = organization?.id ?? null;

  // Synchronously ensure org cookie is set before org-scoped requests.
  if (orgId) {
    const currentCookieOrgId = getCookieValue(ORG_COOKIE_NAME);
    if (currentCookieOrgId !== orgId) {
      setOrgCookie(orgId);
    }
  }

  const accountsEnabled = Boolean(session.data && orgId);
  const {
    accounts,
    isLoading: isAccountsLoading,
    error: accountsError,
  } = useAccounts(undefined, accountsEnabled);

  if (session.isPending) return null;
  if (isChecking) return null;
  if (isOrgLoading) return null;
  if (accountsEnabled && isAccountsLoading) return null;

  if (!session.data) return <Navigate to="/signin" replace />;
  if (!isConnected) return <Navigate to="/register-device" replace />;
  if (!organization) return <Navigate to="/waiting-approval" replace />;
  if (accountsError) throw accountsError;

  // Org has accounts -> this page should not be reachable.
  if (accounts.length > 0) {
    return <Navigate to="/accounts" replace />;
  }

  return <Outlet />;
}
